import { selectHotspotLabelLayerIsVisible } from "features/entity-caching/flights/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    AreaUnit,
    InfraredImageWithContext,
    IntegrationExportEntity,
    isUsable,
    JobStatusType,
    MapPosition,
    scanAreaDefaultHexFillColor,
    scanAreaDefaultHexOutlineColor,
    Licenses,
    hotspotPointFillOpacity8Bit,
    hotspotPointFillOpacity,
    hotspotSizes,
    IntterraImageProduct,
} from "@nova/constants"
import { download, downloadUri } from "tools/browser/download"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { createFlightpathKML } from "@nova/data-create-flight-path-kml"
import { createHotspotsCSV } from "@nova/data-create-hotspots-csv"
import { selectSortedAndFilteredHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { useCallback, useMemo, useState } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { createPolygonKML } from "@nova/data-create-polygon-kml"
import { convertColourToKMLColour } from "@nova/util-strings"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { config } from "@nova/configuration/browser"
import { get } from "nodash"
import {
    DownloadButtonData,
    IntegrationOption,
    NestedDownloadData,
} from "@nova/component-export-download-buttons-hover"
import { hotspotTransformer } from "integrations/geojson/transformers/hotspots"
import { polygonTransformer } from "integrations/geojson/transformers/hotspot-polygons"
import { flightpathTransformer } from "integrations/geojson/transformers/flightpath"
import { FeatureCollection } from "geojson"
import { useIntterraTransfer } from "integrations/intterra/transfer"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { calculateArea, projectToCRS } from "@nova/calculate-geospatial"
import { formatArea, TimeFormat } from "@nova/format"

import { InfraredFlight } from "@nova/protobuf"
import { selectActiveFirePerimeterPolygons } from "features/entity-caching/fire-perimeters/selectors"
import {
    selectWorkspaceDateFormat,
    selectActiveWorkspaceId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { selectUserID } from "user/profile/selectors"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { getDate } from "features/map/inspector-panel/module"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import {
    finished,
    originalZipFilePath,
    outputFolder,
    sampleOriginalZipFilePath,
    sampleOutputFolder,
} from "@nova/util-s3"
import { fetchApiGateway, getApiGatewayDomain } from "@nova/fetch-api-gateway"
import { useCopyResourceLink } from "plugins/shared/useCopyResourceLink"
import { createS3DownloadUrlInBrowser } from "plugins/shared/s3url"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { UploadState } from "@nova/slice-multifile-upload"
import { InfraredImageState } from "./InfraredImageSlice"
import { selectLastSelectedLayer } from "panels/content/side-panel/slice.new"
import { KMLBuilder } from "@nova/kml-builder"
import { getPropertiesForHotspots } from "integrations/geojson/hotspotProperties"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useDataTransfer } from "integrations/transfer/useDataTransfer"

export type ParsedHotspot = {
    name: string
    latitude: number
    longitude: number
    status: string
    confidence: number
    color: [number, number, number, number]
    polygon?: MapPosition[]
    distance: number | null | undefined
    id: string
    createdAt: string
}

export const useDownloadButtonData = (
    uploads: (InfraredImageWithContext | UploadState<InfraredImageState>)[]
): DownloadButtonData => {
    const dispatch = useAppDispatch()

    const flight = useAppSelector(selectLastSelectedLayer) as InfraredFlight
    const id = flight.id
    const userId = useAppSelector(selectUserID)
    const fireId = useAppSelector(selectActivatedFireId)!
    const workspaceId = useAppSelector(selectActiveWorkspaceId())!
    const fire = useAppSelector(selectFireById(fireId))!
    const labelsAreEnabled = useAppSelector(selectHotspotLabelLayerIsVisible(id))
    const isManualUpload = flight.skippedProcessing
    const {
        organizationUsesIntterra,
        intterraIntegration,
        organizationSettingCRS,
        organizationSettingNotation,
    } = config.use(
        "organizationUsesIntterra",
        "intterraIntegration",
        "organizationSettingCRS",
        "organizationSettingNotation"
    )

    const { transfer, integrationName } = useDataTransfer()

    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.agol,
            authType: "oauth",
            userId,
        })

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            workspaceId,
            integration: IntegrationType.tak,
            authType: "certificate",
            userId,
        })

    const { exportToAGOL } = useExportToAGOL()
    const { exportToTAK } = useExportToTAK()

    const polygons = useAppSelector(selectActiveFirePerimeterPolygons)
    const job = flight?.job
    const { transferIntterraData } = useIntterraTransfer()

    const getFilename = useCallback(
        (end: string) => `${fire.name} ${flight ? flight.name + " " : ""}- ${end}`,
        [fire, flight]
    )

    const cogId = flight?.cogOrtho?.fileId
    const isDemo = !!flight.demoId
    const mask = useAppSelector(selectOrthoMaskByFlightId(flight.id))
    const folder = isDemo
        ? sampleOutputFolder(flight.demoId)
        : outputFolder(workspaceId, fireId, flight.id)

    const usableUploads = useMemo(
        () =>
            (uploads?.filter((e) => isUsable(e)) as InfraredImageWithContext[]).sort((a, b) =>
                (a.takenAt || "") < (b.takenAt || "") ? -1 : a.takenAt === b.takenAt ? 0 : 1
            ),
        [uploads]
    )

    const downloadShapefile = useCallback(
        async (data: FeatureCollection, type: string, fileName: string) => {
            const res = await fetch(
                "/api/ogr2ogr?crs=" +
                    coordinateSystems[organizationSettingCRS].code +
                    "&type=" +
                    type,
                {
                    method: "POST",
                    body: new File([new Blob([JSON.stringify(data)])], `${type}.geojson`),
                }
            )
            if (res.status >= 300) {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to create shapefile.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )
            }
            const blob = await res.blob()

            await downloadUri(getFilename(`${fileName}.zip`), URL.createObjectURL(blob))
        },
        [organizationSettingCRS, dispatch, getFilename]
    )
    const hotspots = useAppSelector(selectSortedAndFilteredHotspotsByFlightId(id))

    const downloadFlightPath = useCallback(() => {
        if (!flight) return
        download(
            getFilename("Flightpath.kml"),
            "application/vnd.google-earth.kml+xml",
            createFlightpathKML(flight.name + " Flightpath", [usableUploads])
        )
    }, [flight, getFilename, usableUploads])

    const downloadHeatKML = useCallback(() => {
        const properties = getPropertiesForHotspots(hotspots)
        const kml = new KMLBuilder(getFilename("Heatmap.kml"))
        kml.addStyle("redCircle", {
            icon: { href: "https://cdn.mapnova.com/icons/isolated-heat.png" },
            label: labelsAreEnabled ? undefined : { scale: 0 },
        })
        hotspots.forEach(({ longitude, latitude, color, sizeIndex }, i) => {
            const hotspotSize =
                sizeIndex !== undefined && flight.relativeSizeEnabled
                    ? hotspotSizes[sizeIndex]
                    : undefined
            const separatedColors = color || [255, 0, 0, hotspotPointFillOpacity8Bit] // #FF0000
            if (separatedColors && separatedColors.length === 4)
                separatedColors[3] *= hotspotPointFillOpacity
            else separatedColors?.push(hotspotPointFillOpacity8Bit)
            kml.addPoint(
                { longitude, latitude },
                {
                    styleId: "redCircle",
                    properties: properties[i],
                    description: JSON.stringify({
                        color: separatedColors.join(", "),
                        relativeSize: hotspotSize,
                    }),
                    name: (i + 1).toString(),
                }
            )
        })
        download(getFilename("Heatmap.kml"), "application/vnd.google-earth.kml+xml", kml.write())
    }, [hotspots, labelsAreEnabled, getFilename, flight])

    const downloadPolygonKML = useCallback(() => {
        if (!flight) return
        download(
            getFilename("Polygons.kml"),
            "application/vnd.google-earth.kml+xml",
            createPolygonKML(
                "polygons",
                hotspots
                    .filter((hotspot) => hotspot.polygon)
                    .map(({ polygon }) => ({
                        coords: polygon.map((p) => [p.longitude, p.latitude]),
                        width: 0,
                        fill: flight.polyFill
                            ? convertColourToKMLColour(decodeNumberToColor(flight.polyFill))
                            : "7F4656A4",
                        color: flight.polyStroke
                            ? convertColourToKMLColour(decodeNumberToColor(flight.polyStroke))
                            : "FF4C60C2",
                    }))
            )
        )
    }, [flight, getFilename, hotspots])

    const downloadScanAreaPolygonKML = useCallback(() => {
        if (!flight) return
        const scanAreaPolygon = flight.job?.scanArea
        if (!scanAreaPolygon) return
        download(
            getFilename("ScanArea.kml"),
            "application/vnd.google-earth.kml+xml",
            createPolygonKML("scanArea", [
                {
                    coords: scanAreaPolygon.map((p) => [p.longitude, p.latitude]),
                    width: 0,
                    fill: flight.scanAreaFillColor
                        ? convertColourToKMLColour(decodeNumberToColor(flight.scanAreaFillColor))
                        : convertColourToKMLColour(scanAreaDefaultHexFillColor),
                    color: flight.scanAreaOutlineColor
                        ? convertColourToKMLColour(decodeNumberToColor(flight.scanAreaOutlineColor))
                        : convertColourToKMLColour(scanAreaDefaultHexOutlineColor),
                },
            ])
        )
    }, [flight, getFilename])

    const downloadScanAreaShp = useCallback(() => {
        if (!flight) return
        const scanAreaPolygon = flight.job?.scanArea
        if (!scanAreaPolygon) return
        downloadShapefile(
            {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: {
                            type: "Polygon",
                            coordinates: [
                                scanAreaPolygon.map((p) =>
                                    projectToCRS(
                                        [p.longitude, p.latitude],
                                        coordinateSystems[organizationSettingCRS].proj4
                                    )
                                ),
                            ],
                        },
                        properties: {
                            id: flight.id,
                        },
                    },
                ],
            },
            "Polygon",
            "Scan Area"
        )
    }, [downloadShapefile, flight, organizationSettingCRS])

    const downloadHeatCSV = useCallback(() => {
        download(
            getFilename("Heatmap.csv"),
            "text/csv",
            createHotspotsCSV(
                hotspots,
                organizationSettingNotation,
                coordinateSystems[organizationSettingCRS].proj4
            )
        )
    }, [getFilename, organizationSettingCRS, organizationSettingNotation, hotspots])

    const downloadImages = async () => {
        const filename = getFilename("Images.zip")
        const url = await createS3DownloadUrlInBrowser(
            isDemo
                ? sampleOriginalZipFilePath(flight.demoId)
                : originalZipFilePath(workspaceId, fireId, id),
            isDemo
        )
        await downloadUri(filename, url)
    }

    const downloadTiff = async () => {
        const filename = getFilename("Orthophoto.tif")
        const s3Key = finished(folder, undefined, undefined, cogId)
        const url = await createS3DownloadUrlInBrowser(s3Key, isDemo)
        await downloadUri(filename, url)
    }
    const downloadLRTiff = async () => {
        const filename = getFilename("Orthophoto_Compressed.tif")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "tif", true), isDemo)
        await downloadUri(filename, url)
    }
    const downloadJpg = async () => {
        const filename = getFilename("Orthophoto.jpg")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "jpg"), isDemo)
        await downloadUri(filename, url)
    }
    const downloadPng = async () => {
        const filename = getFilename("Orthophoto.png")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "png"), isDemo)
        await downloadUri(filename, url)
    }

    const downloadKmz = async () => {
        const filename = getFilename("Orthophoto.kmz")
        const url = await createS3DownloadUrlInBrowser(finished(folder, "kmz"), isDemo)
        await downloadUri(filename, url)
    }

    const downloadCogExport = async (
        format: "jpeg" | "png" | "webp" | "kmz" | "tiff",
        maskPolygon?: [number, number][],
        maxSize?: number
    ) => {
        if (!workspaceId || !fireId) {
            console.error("cannot export due to missing ids", { workspaceId, fireId })
            return
        }
        const ext = { jpeg: "jpg", png: "png", webp: "webp", kmz: "kmz", tiff: "tif" } as const
        const filename = getFilename(
            maskPolygon ? `OrthophotoCropped.${ext[format]}` : `Orthophoto.${ext[format]}`
        )
        const params = new URLSearchParams({ format, isDemo: String(isDemo), filename })
        if (cogId) params.set("cogId", cogId)
        if (maskPolygon) params.set("polygon", JSON.stringify(maskPolygon))
        if (maxSize) params.set("maxSize", String(maxSize))
        const url = `${getApiGatewayDomain()}/org/${workspaceId}/project/${fireId}/flight/${flight.demoId || flight.id}/cog-export?${params}`
        downloadUri(filename, url)
    }

    const copyResourceLink = useCopyResourceLink({
        workspaceId,
        fireId,
        flightId: flight.demoId || flight.id,
        isDemo,
    })

    const generateStableKmz = (maskPolygon?: [number, number][]) => {
        const params = new URLSearchParams({
            format: "kmz",
            stable: "true",
            isDemo: String(isDemo),
            maxSize: "2000",
        })
        if (cogId) params.set("cogId", cogId)
        if (maskPolygon) params.set("polygon", JSON.stringify(maskPolygon))
        return fetchApiGateway("get /org/:orgId/project/:projectId/flight/:flightId/cog-export", {
            params: { orgId: workspaceId, projectId: fireId, flightId: flight.demoId || flight.id },
            query: params.toString(),
        })
    }

    const downloadIrImagesZip = async () => {
        const filename = getFilename("Images.zip")
        const params = new URLSearchParams({ type: "ir", isDemo: String(isDemo) })
        const res = await fetch(
            `/api/organization/${workspaceId}/project/${fireId}/flight/${flight.demoId || flight.id}/images-zip?${params}`
        )
        if (!res.ok) return
        const blob = await res.blob()
        downloadUri(filename, URL.createObjectURL(blob))
    }

    const downloadHeatShp = useCallback(
        () =>
            downloadShapefile(
                hotspotTransformer(hotspots, coordinateSystems[organizationSettingCRS].proj4),
                "Points",
                "Hotspots"
            ),
        [hotspots, organizationSettingCRS, downloadShapefile]
    )

    const downloadPolygonShp = useCallback(
        () =>
            downloadShapefile(
                polygonTransformer(hotspots, coordinateSystems[organizationSettingCRS].proj4),
                "Polygons",
                "Burn Area"
            ),
        [hotspots, organizationSettingCRS, downloadShapefile]
    )

    const downloadFlightpathShp = useCallback(
        () =>
            downloadShapefile(
                flightpathTransformer(
                    [usableUploads],
                    coordinateSystems[organizationSettingCRS].proj4
                ),
                "Flightpath",
                "Flight Path"
            ),
        [usableUploads, organizationSettingCRS, downloadShapefile]
    )
    const baseOptions: NestedDownloadData = {}
    let integrationOptions: Record<string, IntegrationOption> | undefined = undefined

    const [agolExportError, setAGOLExportError] = useState<string>("Failed to export")

    const extraFeaturesJSON = get(job, "extraFeatures") as string | undefined
    const extraFeatures = extraFeaturesJSON
        ? (JSON.parse(extraFeaturesJSON.replaceAll("'", '"')) as string[])
        : null

    if (!isManualUpload)
        baseOptions["Flight Path"] = {
            KML: { callback: downloadFlightPath, restrictedLicenses: [Licenses.Lite] },
            SHP: {
                callback: downloadFlightpathShp,
                restrictedLicenses: [Licenses.Lite],
            },
        }

    if (extraFeatures?.includes("scanArea")) {
        baseOptions["Scan Area"] = {
            KML: {
                callback: downloadScanAreaPolygonKML,
                restrictedLicenses: [Licenses.Lite],
            },
            SHP: { callback: downloadScanAreaShp, restrictedLicenses: [Licenses.Lite] },
        }
    }

    if (job) {
        if (job.status === JobStatusType.COMPLETED) {
            baseOptions.Hotspots = {
                KML: { callback: downloadHeatKML, restrictedLicenses: [Licenses.Lite] },
                CSV: { callback: downloadHeatCSV, restrictedLicenses: [Licenses.Lite] },
                SHP: { callback: downloadHeatShp, restrictedLicenses: [Licenses.Lite] },
            }

            if (!isManualUpload) {
                baseOptions["Hot Areas"] = {
                    KML: { callback: downloadPolygonKML, restrictedLicenses: [Licenses.Lite] },
                    SHP: { callback: downloadPolygonShp, restrictedLicenses: [Licenses.Lite] },
                }

                baseOptions["IR Orthomosaic"] = {
                    TIFF: { callback: downloadTiff, restrictedLicenses: [Licenses.Lite] },
                }
            }
        }

        if (extraFeatures?.includes("originalsZip")) {
            baseOptions.Images = {
                ZIP: { callback: downloadImages, restrictedLicenses: [Licenses.Lite] },
            }
        }

        if (extraFeatures?.includes("smallTiff")) {
            baseOptions["IR Orthomosaic"] = {
                ...baseOptions["IR Orthomosaic"],
                ["SMALL TIFF"]: {
                    callback: downloadLRTiff,
                    restrictedLicenses: [Licenses.Lite],
                },
            }
        }
        if (extraFeatures?.includes("orthoJpg")) {
            baseOptions["IR Orthomosaic"] = {
                ...baseOptions["IR Orthomosaic"],
                JPG: { callback: downloadJpg, restrictedLicenses: [Licenses.Lite] },
            }
        }
        if (extraFeatures?.includes("orthoPng")) {
            baseOptions["IR Orthomosaic"] = {
                ...baseOptions["IR Orthomosaic"],
                PNG: { callback: downloadPng, restrictedLicenses: [Licenses.Lite] },
            }
        }
        if (extraFeatures?.includes("orthoKmz")) {
            baseOptions["IR Orthomosaic"] = {
                ...baseOptions["IR Orthomosaic"],
                KMZ: {
                    callback: downloadKmz,
                    restrictedLicenses: [Licenses.Lite],
                    ...(extraFeatures?.includes("cog")
                        ? {
                              copyCallback: (id: string) =>
                                  copyResourceLink(id, "arc-ortho.kmz", () => generateStableKmz()),
                          }
                        : extraFeatures?.includes("orthoKmzLink")
                          ? {
                                copyCallback: (id: string) => copyResourceLink(id, "arc-ortho.kmz"),
                            }
                          : {}),
                },
            }
        }

        if (extraFeatures?.includes("cog")) {
            baseOptions["IR Orthomosaic"] = {
                ["JPEG (Medium)"]: {
                    callback: () => downloadCogExport("jpeg", undefined, 8000),
                    restrictedLicenses: [Licenses.Lite],
                },
                ...baseOptions["IR Orthomosaic"],
            }
        }

        if (extraFeatures?.includes("cog") && cogId) {
            baseOptions["IR Orthomosaic"] = {
                ["SMALL TIFF"]: {
                    callback: () => downloadCogExport("tiff"),
                    restrictedLicenses: [Licenses.Lite],
                },
                JPEG: {
                    callback: () => downloadCogExport("jpeg"),
                    restrictedLicenses: [Licenses.Lite],
                },
                PNG: {
                    callback: () => downloadCogExport("png"),
                    restrictedLicenses: [Licenses.Lite],
                },
                KMZ: {
                    callback: () => downloadCogExport("kmz"),
                    copyCallback: (id) =>
                        copyResourceLink(id, "arc-ortho.kmz", () => generateStableKmz()),
                    restrictedLicenses: [Licenses.Lite],
                },
                ...baseOptions["IR Orthomosaic"],
            }
            if (!baseOptions.Images) {
                baseOptions.Images = {
                    ZIP: {
                        callback: downloadIrImagesZip,
                        restrictedLicenses: [Licenses.Lite],
                    },
                }
            }
        }

        if (extraFeatures?.includes("cog") && mask?.polygon) {
            const maskPoly = mask.polygon.coordinates.map((c): [number, number] => [
                c.longitude,
                c.latitude,
            ])
            baseOptions["IR Orthomosaic (Cropped)"] = {
                ["SMALL TIFF"]: {
                    callback: () => downloadCogExport("tiff", maskPoly),
                    restrictedLicenses: [Licenses.Lite],
                },
                JPEG: {
                    callback: () => downloadCogExport("jpeg", maskPoly),
                    restrictedLicenses: [Licenses.Lite],
                },
                ["JPEG (Medium)"]: {
                    callback: () => downloadCogExport("jpeg", maskPoly, 8000),
                    restrictedLicenses: [Licenses.Lite],
                },
                PNG: {
                    callback: () => downloadCogExport("png", maskPoly),
                    restrictedLicenses: [Licenses.Lite],
                },
                KMZ: {
                    callback: () => downloadCogExport("kmz", maskPoly),
                    copyCallback: (id) =>
                        copyResourceLink(id, "arc-ortho-cropped.kmz", () =>
                            generateStableKmz(maskPoly)
                        ),
                    restrictedLicenses: [Licenses.Lite],
                },
            }
        }
    }
    /*
     * We store the name of the secret in AWS Secrets manager in Dynamo as a Portfolio setting
     * passed server side, this secret is retrieved and contains the key/secret, and bucket details
     * */
    if (organizationUsesIntterra) {
        if (intterraIntegration && workspaceId && fireId && userId) {
            integrationOptions ??= {}
            integrationOptions.Intterra = {
                callback: async () => {
                    if (!flight) throw "No flight"
                    let area = undefined
                    if (polygons.length > 0) {
                        const meters = polygons.reduce(
                            (acc, cur) => acc + calculateArea(cur.coordinates),
                            0
                        )
                        area = Math.round(
                            parseFloat(
                                formatArea({ value: meters, unit: AreaUnit.Acres }).slice(0, -3)
                            )
                        ).toFixed(0)
                    }

                    await transferIntterraData({
                        workspaceId,
                        fireId,
                        flightIds: [flight.id],
                        imageType: IntterraImageProduct.IR,
                        perimeterArea: area,
                    })

                    dispatch(
                        updateInfraredFlight({
                            id: flight.id,
                            updates: {},
                            context: {
                                updateIntegrationId: "intterra",
                            },
                        })
                    )
                },
                successMessage: "Sent to Intterra",
                failureMessage: "Failed to connect to Intterra",
                key: "intterra",
            }
        }
    }
    if (integrationName) {
        integrationOptions ??= {}
        integrationOptions[integrationName] = {
            callback: async () => {
                await transfer("ir", [id])

                dispatch(
                    updateInfraredFlight({
                        id: flight.id,
                        updates: {},
                        context: {
                            updateIntegrationId: "azure",
                        },
                    })
                )
            },
            successMessage: "Transfer job started",
            failureMessage: "Failed to initiate transfer",
            key: "azure",
        }
    }
    if (agolAuths?.at(0) && fireId && workspaceId) {
        integrationOptions ??= {}
        const auth = agolAuths.at(0)
        const settings = auth?.settings as AGOLSettings
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    setAGOLExportError("Internal error occured")
                }

                if (isAuthExpired(auth)) {
                    try {
                        await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    } catch (e) {
                        setAGOLExportError("Failed to authenticate")
                        throw e
                    }
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }

                return exportToAGOL({
                    entities: [
                        {
                            id: id,
                            type: IntegrationExportEntity.InfraredFlight,
                        },
                    ],
                    projectId: fireId,
                    workspaceId,
                })
                    .then(() => {
                        invalidateAGOL()
                    })
                    .catch((e) => {
                        setAGOLExportError("Failed to export")
                        throw e
                    })
            },
            successMessage: "Success",
            failureMessage: agolExportError,
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : null
            },
        }
    }
    if (takAuths?.at(0) && fireId && workspaceId) {
        integrationOptions ??= {}
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[flight.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!flight) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    entities: [
                        {
                            id: id,
                            type: IntegrationExportEntity.InfraredFlight,
                            options: { disableLabels: !labelsAreEnabled },
                        },
                    ],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : null
            },
        }
    }
    return {
        exportOptions: baseOptions,
        integrationOptions,
    }
}
